/**
 * Location Detail Page Component
 * /admin/locations/:id
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import apiClient from '../api/client';

const LocationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadLocation();
  }, [id]);

  const loadLocation = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.getLocation(id);
      setLocation(data);
    } catch (err) {
      setError(err.message || 'Failed to load location details');
    } finally {
      setLoading(false);
    }
  };

  const getLocationTypeColor = (type) => {
    const colors = {
      airport: 'primary',
      hotel: 'secondary',
      office: 'info',
      camp: 'warning',
      residential: 'success',
      other: 'default',
    };
    return colors[type] || 'default';
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !location) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/locations')}>
          Back to Locations
        </Button>
      </Container>
    );
  }

  if (!location) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h6">Location not found</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/locations')}>
          Back to Locations
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/locations')}
            sx={{ mb: 1 }}
          >
            Back to Locations
          </Button>
          <Typography variant="h4" component="h1">
            Location Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Location ID: {location.id.substring(0, 8)}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/admin/locations/${location.id}/edit`)}
        >
          Edit Location
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Name:
                </Typography>
                <Typography variant="body1">{location.name}</Typography>
              </Box>
              {location.address && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Address:
                  </Typography>
                  <Typography variant="body1">{location.address}</Typography>
                </Box>
              )}
              {location.city && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    City:
                  </Typography>
                  <Typography variant="body1">{location.city}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Type:
                </Typography>
                <Chip
                  label={location.location_type || 'other'}
                  color={getLocationTypeColor(location.location_type)}
                  sx={{ mt: 0.5 }}
                />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Status:
                </Typography>
                <Chip
                  label={location.is_active ? 'Active' : 'Inactive'}
                  color={location.is_active ? 'success' : 'default'}
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Coordinates */}
        {(location.latitude || location.longitude) && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Coordinates
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" flexDirection="column" gap={2}>
                {location.latitude && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Latitude:
                    </Typography>
                    <Typography variant="body1">{location.latitude}</Typography>
                  </Box>
                )}
                {location.longitude && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Longitude:
                    </Typography>
                    <Typography variant="body1">{location.longitude}</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Aliases */}
        {location.aliases && Array.isArray(location.aliases) && location.aliases.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Aliases
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" gap={1} flexWrap="wrap">
                {location.aliases.map((alias, index) => (
                  <Chip key={index} label={alias} variant="outlined" />
                ))}
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default LocationDetail;

